import siteImages from '../../public/mocks/site-images.json';

export interface Photo {
  id: string;
  url: string;
  altText: string;
  caption?: string;
  title?: string;
  category?: string;
}

/**
 * The image registry, imported at build time.
 *
 * This used to be fetched over the network: every <Image> on a page requested
 * the whole 54 kB /mocks/site-images.json on its own, after hydration, just to
 * find one entry — 21 requests on the design-materials page — and served with
 * max-age=0 so each one revalidated. Two of the helpers also slept for a fixed
 * 300 ms first, a leftover from when this mocked a slow WordPress API.
 *
 * Importing it puts the lookup in memory: no request, no wait, and because the
 * answer is available on the first render, the prerendered HTML already
 * carries the real <img src>, so the browser starts loading pictures with the
 * page instead of after the JavaScript has run.
 *
 * public/mocks/site-images.json stays the single source of truth and is still
 * served as a file.
 */
const REGISTRY: Photo[] = siteImages as Photo[];
const BY_ID = new Map<string, Photo>(REGISTRY.map((p) => [p.id, p]));

/** Synchronous lookup — the same answer on the server and in the browser. */
export const getImageByIdSync = (id: string): Photo | null => BY_ID.get(id) ?? null;

export const getImagesByCategory = async (category: string): Promise<Photo[]> =>
  REGISTRY.filter((img) => img.category === category);

/** Gallery photos live in a separate, rarely used file; fetched without delay. */
export const getGalleryPhotos = async (): Promise<Photo[]> => {
  try {
    const response = await fetch('/mocks/gallery-photos.json');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return (await response.json()) as Photo[];
  } catch (error) {
    console.error('Could not load gallery photos:', error);
    throw error;
  }
};
